import React from "react";
import LogoImg from "../../images/LogoImg";
import { usePartner } from "../../hooks/usePartner";
import { Typography } from "../common";

interface CobrandedHeaderProps {
  /**
   * Whether to show the "Powered by" text
   * @default true
   */
  showPoweredBy?: boolean;

  /**
   * Custom class name for the header container
   */
  className?: string;
}

/**
 * Co-branded header component that displays partner logo alongside Pedagogue logo.
 * Only renders when a valid partner_id is present in the URL.
 */
const CobrandedHeader: React.FC<CobrandedHeaderProps> = ({
  showPoweredBy = true,
  className = "",
}) => {
  const { partner, isPartnerBranded } = usePartner();

  // Don't render if not partner-branded
  if (!isPartnerBranded) {
    return null;
  }

  return (
    <div
      className={`bg-white border-b border-slate-200 py-3 px-4 md:px-8 ${className}`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Partner Logo */}
        <div className="flex items-center gap-4">
          <img
            src={partner.logoUrl}
            alt={partner.logoAlt}
            width={partner.logoWidth}
            className="h-10 w-auto object-contain"
            onError={(e) => {
              // Fallback if logo fails to load
              e.currentTarget.style.display = "none";
            }}
          />

          {/* Divider */}
          <div className="h-8 w-px bg-slate-300" />

          {/* Pedagogue Logo */}
          <LogoImg
            width={100}
            height={20}
            className="h-5 w-auto object-contain"
          />
        </div>

        {/* Powered by text */}
        {showPoweredBy && (
          <Typography variant="small" className="text-textSecondary hidden sm:block">
            Learning distributed by{" "}
            <span className="font-medium text-textPrimary">{partner.headerBrandName || partner.name}</span>{" "}
            platform
          </Typography>
        )}
      </div>
    </div>
  );
};

export default CobrandedHeader;
